import { isEqual } from 'es-toolkit';
import React from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Checkbox, Divider, FormControlLabel, Stack } from '@mui/material';

import { INDETERMINATE } from '@paragon/client-tools';
import { WorkLine } from '@paragon/contract-library';
import { PropertiesPanel } from '@paragon/layout-models';

import { SelectableItemEntry } from '../../../../library/selection/types';
import { TransformationButtons } from '../../../../library/transformations/controls/TransformationButtons';
import { DeletionActionFactory, getDeletionProps } from '../../../../store/actions/deletionActions';
import { setWorkLineScope, toggleWorkLineBoundingStatus } from '../../../../store/actions/layout/workLineActions';
import { isLayoutModeViewOnly } from '../../../../store/selectors/layoutModeSelectors';
import { propertiesShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { PropertiesPanelMenuButton } from './PropertiesPanelMenuButton';
import { WorkLineScopeSelect } from './WorkLineScopeSelect';

export function WorkLineProperties(props: { entries: SelectableItemEntry[] }) {
  const dispatch = useAppDispatch();

  const viewOnlyProperties = useAppSelector(propertiesShouldBeViewOnly);
  const viewOnlyLayout = useAppSelector(isLayoutModeViewOnly);
  const viewOnly = viewOnlyProperties || viewOnlyLayout;

  const deletionProps = useAppSelector(getDeletionProps);

  const deletionActionFactory = new DeletionActionFactory(props.entries, deletionProps);

  if (props.entries.some((entry) => entry.type !== 'workLine')) throw new Error('That is not a work line');
  const workLines = props.entries.map((entry) => entry.item as WorkLine);

  return (
    <PropertiesPanel
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      title="Work Line Properties"
      titleButton={<PropertiesPanelMenuButton type="workLine" guid={props.entries[0].item.guid} />}
    >
      <TransformationButtons />

      <Divider />

      <WorkLineScopeSelect
        value={
          workLines.every((workLine) => isEqual(workLine.scope, workLines[0].scope))
            ? workLines[0].scope
            : INDETERMINATE
        }
        disabled={viewOnly}
        onChange={async (scope) => {
          await dispatch(
            setWorkLineScope(
              workLines.map((workLine) => workLine.guid),
              scope,
            ),
          );
        }}
      />

      <FormControlLabel
        label="Use for bounding truss envelopes"
        disabled={viewOnly}
        control={
          <Checkbox
            color="default"
            checked={workLines.every((workLine) => workLine.bounding)}
            indeterminate={
              workLines.some((workLine) => workLine.bounding) && !workLines.every((workLine) => workLine.bounding)
            }
            onChange={async () => {
              await dispatch(toggleWorkLineBoundingStatus(workLines.map((workLine) => workLine.guid)));
            }}
          />
        }
      />

      <Stack direction="row" sx={{ marginY: 1 }}>
        <Button
          variant="outlined"
          startIcon={<DeleteIcon />}
          sx={{ marginLeft: 'auto' }}
          disabled={!deletionActionFactory.isDeletionFeasible()}
          onClick={() => {
            dispatch(deletionActionFactory.getDeletionAction());
          }}
        >
          Delete Work Line{props.entries.length > 1 ? 's' : ''}
        </Button>
      </Stack>
    </PropertiesPanel>
  );
}
